import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faArrowUp,
  faArrowDown,
  faMinus,
  faEnvelope,
  faEnvelopeOpenText,
  faFlagCheckered
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import './FbBudgetAlertHistory.css';

// Currencies Meta treats as zero-decimal (no /100 conversion). Mirrors the
// backend list in services/fbBudgetMonitorService.js.
const ZERO_DECIMAL_CURRENCIES = new Set([
  'CLP', 'COP', 'CRC', 'HUF', 'ISK', 'IDR', 'JPY', 'KRW', 'PYG', 'TWD', 'VND'
]);

const formatBudgetMinor = (minor, currency) => {
  if (minor === null || minor === undefined) return 'Not set';
  // Unknown currency -> unknown minor-unit offset; show the raw value rather
  // than a possibly-100x-wrong conversion.
  if (!currency) return `${Number(minor)} (minor units)`;
  const code = currency.toUpperCase();
  const value = ZERO_DECIMAL_CURRENCIES.has(code) ? Number(minor) : Number(minor) / 100;
  return `${code} ${value.toFixed(2)}`;
};

/**
 * FbBudgetAlertHistory - Campaign-details section for the hourly FB daily
 * budget monitor (enable-import-campaign-fb-daily-budget-alert account flag).
 * The user turns monitoring on/off per campaign via the toggle here (monitors
 * are not auto-created on import); checks run only within the FB campaign's
 * flight window in the campaign's timezone. Shows the monitor status and the
 * history of detected budget changes.
 */
const FbBudgetAlertHistory = ({ campaign, accountId, user }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [toggleError, setToggleError] = useState(null);

  const fetchAlerts = useCallback(async () => {
    if (!campaign?.id || !accountId) return;

    try {
      setIsLoading(true);
      const response = await api.get(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/fb-budget-alerts`),
        { params: { accountId, userId: user?.userId || user?.id, limit: 50 } }
      );
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching FB budget alerts:', err);
      setError('Failed to load budget alert history');
    } finally {
      setIsLoading(false);
    }
  }, [campaign?.id, accountId, user]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // On = create/reactivate the monitor (fresh baseline snapshot); off = pause
  // it in place. History and snapshot are kept across off/on cycles.
  const handleToggleMonitor = async (enable) => {
    try {
      setIsToggling(true);
      setToggleError(null);
      await api.post(
        getApiUrl(`/api/v2/campaigns/${campaign.id}/fb-budget-monitor${enable ? '' : '/pause'}`),
        {},
        { params: { accountId, userId: user?.userId || user?.id } }
      );
      await fetchAlerts();
    } catch (err) {
      console.error(`Error ${enable ? 'enabling' : 'pausing'} FB budget monitor:`, err);
      setToggleError(err.response?.data?.error || `Failed to ${enable ? 'enable' : 'disable'} budget alerts`);
    } finally {
      setIsToggling(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Flight boundaries are shown in the campaign's (ad account's) timezone
  // when the browser knows it; otherwise fall back to the viewer's local time.
  const formatFlightDate = (dateString, timezone) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    try {
      return date.toLocaleString(undefined, { timeZone: timezone || undefined, dateStyle: 'medium', timeStyle: 'short' });
    } catch (e) {
      return date.toLocaleString();
    }
  };

  const getFlightLabel = (monitor) => {
    if (!monitor?.flightStartAt && !monitor?.flightEndAt) return null;
    const start = formatFlightDate(monitor.flightStartAt, monitor.timezone) || '…';
    const end = formatFlightDate(monitor.flightEndAt, monitor.timezone) || 'no end date';
    return `Flight: ${start} – ${end}${monitor.timezone ? ` (${monitor.timezone})` : ''}`;
  };

  const getChangeIndicator = (alert) => {
    if (alert.previousBudgetMinor === null || alert.newBudgetMinor === null) return null;
    const isIncrease = alert.newBudgetMinor > alert.previousBudgetMinor;
    const isDecrease = alert.newBudgetMinor < alert.previousBudgetMinor;
    return (
      <span className={`budget-change ${isIncrease ? 'increase' : isDecrease ? 'decrease' : ''}`}>
        <FontAwesomeIcon icon={isIncrease ? faArrowUp : isDecrease ? faArrowDown : faMinus} />
        {alert.changePercent !== null ? ` ${Math.abs(alert.changePercent).toFixed(1)}%` : ''}
      </span>
    );
  };

  const getMonitorBadge = (monitor) => {
    if (!monitor) {
      return <span className="monitor-badge inactive">Not monitored</span>;
    }
    if (monitor.status === 'completed') {
      return (
        <span className="monitor-badge inactive">
          <FontAwesomeIcon icon={faFlagCheckered} /> Flight ended
        </span>
      );
    }
    if (monitor.status !== 'active') {
      return <span className="monitor-badge inactive">Paused</span>;
    }
    if (monitor.consecutiveErrorCount > 0) {
      return (
        <span className="monitor-badge warning" title={monitor.lastError || undefined}>
          <FontAwesomeIcon icon={faExclamationCircle} /> Check errors
        </span>
      );
    }
    if (monitor.flightStartAt && new Date(monitor.flightStartAt) > new Date()) {
      return (
        <span className="monitor-badge active">
          <FontAwesomeIcon icon={faCheckCircle} /> Starts with flight
        </span>
      );
    }
    return (
      <span className="monitor-badge active">
        <FontAwesomeIcon icon={faCheckCircle} /> Monitoring hourly
      </span>
    );
  };

  // Feature disabled for this account/campaign — render nothing (parent also
  // gates on the account setting; this covers stale settings caches).
  if (!isLoading && data && data.enabled === false) return null;

  const monitor = data?.monitor || null;
  const alerts = data?.alerts || [];

  // Toggle reflects whether the hourly monitor is running. Off covers: never
  // created, paused (incl. auto-paused after repeated errors), or completed.
  // Turning it on always goes through the create endpoint, which reactivates
  // with a fresh baseline and refetched flight window (so an extended
  // campaign resumes monitoring).
  const alertsOn = !!monitor && monitor.status === 'active';

  return (
    <div className="fb-budget-alert-section">
      <div className="section-header">
        <h3><FontAwesomeIcon icon={faBell} /> FB Daily Budget Alerts</h3>
        <div className="header-actions">
          {!isLoading && !error && getMonitorBadge(monitor)}
          {isToggling && <FontAwesomeIcon icon={faSpinner} spin />}
          {!isLoading && !error && (
            <label className="alerts-toggle" title={alertsOn ? 'Turn off budget alerts' : 'Turn on budget alerts'}>
              <input
                type="checkbox"
                className="toggle-checkbox"
                checked={alertsOn}
                disabled={isToggling}
                onChange={(e) => handleToggleMonitor(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          )}
        </div>
      </div>

      {toggleError && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationCircle} /> {toggleError}
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading budget alert history...
        </div>
      ) : error ? (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationCircle} /> {error}
        </div>
      ) : (
        <>
          {monitor && (
            <div className="monitor-meta">
              {getFlightLabel(monitor) && <span>{getFlightLabel(monitor)}</span>}
              <span>Last checked: {formatDateTime(monitor.lastCheckedAt)}</span>
              <span>Checks: {monitor.checkCount || 0}</span>
              {monitor.lastChangeAt && <span>Last change: {formatDateTime(monitor.lastChangeAt)}</span>}
            </div>
          )}

          {alerts.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faBell} size="2x" style={{ color: '#9ca3af', marginBottom: '8px' }} />
              {monitor ? (
                <>
                  <p>No budget changes detected yet</p>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>
                    The daily budget of this imported Facebook campaign is checked hourly
                    during its flight. Any change is listed here and emailed to the
                    campaign owner.
                  </p>
                </>
              ) : (
                <>
                  <p>Budget alerts are not enabled for this campaign</p>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>
                    Turn on the toggle to check this campaign's Facebook daily budget
                    hourly during its flight and email the campaign owner on any change.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="alerts-table-container">
              <table className="alerts-table">
                <thead>
                  <tr>
                    <th>Detected</th>
                    <th>Changed</th>
                    <th>Budget Change</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(alert => (
                    <tr key={alert.id}>
                      <td>{formatDateTime(alert.detectedAt)}</td>
                      <td>
                        {alert.budgetType === 'adset_daily' ? (
                          <>
                            <span className="target-type">Ad set</span>
                            <span className="target-name">{alert.adsetName || alert.adsetId}</span>
                          </>
                        ) : (
                          <>
                            <span className="target-type">Campaign</span>
                            <span className="target-name">{alert.fbCampaignName || alert.fbCampaignId}</span>
                          </>
                        )}
                      </td>
                      <td>
                        <div className="budget-info">
                          <span>{formatBudgetMinor(alert.previousBudgetMinor, alert.currency)}</span>
                          <span className="arrow">→</span>
                          <span>{formatBudgetMinor(alert.newBudgetMinor, alert.currency)}</span>
                          {getChangeIndicator(alert)}
                        </div>
                      </td>
                      <td>
                        {alert.emailSent ? (
                          <span className="email-status sent" title={alert.emailRecipient || undefined}>
                            <FontAwesomeIcon icon={faEnvelopeOpenText} /> Sent
                          </span>
                        ) : (
                          <span className="email-status failed" title={alert.emailError || 'Email not sent'}>
                            <FontAwesomeIcon icon={faEnvelope} /> Not sent
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FbBudgetAlertHistory;
